import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ApiSdkService } from 'src/app/services/api-sdk.service';
import { CeoOpenProjectService } from '../../ceo-open-project.service';
import { CeoV2Service } from '../../ceo-v2/ceo-v2.service';
import { projectCategoryLabel, projectCategoryStyle, projectCategoryTitle } from '../../project-categories';
import { CeoPlanChangeI, CeoPlanChangeUsageI, CeoProjectI, CeoProjectLimitTierI, CeoProjectLimitsI, CeoPurchaseI, CeoUserDetailI } from 'src/app/interfaces/ceo.interface';
import {
  SELF_REPORTED_CAMPAIGN,
  acquisitionChipClass,
  acquisitionKindFor,
  countryName,
  formatCredits,
  formatDate,
  formatEuros,
  formatNumber,
  hostColor,
  planStyle,
  platformPlanLabel,
  projectTypeLabel,
} from '../../ceo-theme';
import { environment } from 'src/environments/environment';

// Projects is not a tab: it renders as its own section right under the plan.
type DetailTab = 'purchases' | 'acquisition' | 'credits' | 'payments';

interface AcquisitionRow {
  label: string;
  value: any;
  mono?: boolean;
  /** Spans both columns — long values (URLs, the user agent, the tag list). */
  wide?: boolean;
  tone?: 'danger' | 'warn' | 'muted';
}
interface AcquisitionGroup {
  title: string;
  rows: AcquisitionRow[];
  note?: string;
}

/**
 * Slide-over panel for a single customer: profile, acquisition snapshot, spend, projects,
 * and the credit controls.
 *
 * Everything here is read-only except giving/removing credits — the one action the CEO needs
 * from this view. It calls the CEO dashboard's credit endpoints, which reuse the exact same
 * CreditsService + credit_purchases audit trail as the support chat.
 */
@Component({
  selector: 'app-ceo-user-detail',
  templateUrl: './ceo-user-detail.component.html',
  styleUrls: ['./ceo-user-detail.component.scss'],
})
export class CeoUserDetailComponent implements OnChanges {
  /** Opening is driven by the userId: set it to load, null to close. */
  @Input() userId: string | null = null;
  @Output() closed = new EventEmitter<void>();
  /** Emitted after a credit adjustment so the parent table can refresh the row. */
  @Output() creditsChanged = new EventEmitter<void>();

  detail: CeoUserDetailI | null = null;
  /** The "Contact" email modal. Closed again whenever another customer is opened. */
  contactOpen = false;
  loading = false;
  errorMessage = '';

  activeTab: DetailTab = 'purchases';

  // Projects list: a heavy user can own hundreds, so show a window and let the CEO search.
  projectSearch = '';
  projectsShown = 6;
  private readonly PROJECTS_STEP = 12;

  // Plan panel (NEW PLATFORM accounts only)
  planChoice = '';
  planReason = '';
  planSubmitting = false;
  planFeedback = '';
  planError = '';
  planUsage: CeoPlanChangeUsageI | null = null;

  /**
   * ── PROJECT LIMITS (NEW PLATFORM accounts only) ─────────────────────────────
   *
   * ⚠️ THE FORM HOLDS `number | null`, AND `null` IS NOT `0`. An empty box means
   * "no override — use the plan's number" and is sent as `null`; a typed `0` is a
   * real, enforced limit (`0` projects per minute freezes creation on this one
   * account). Anything that collapses the two — `||`, a `0` default, a truthiness
   * test — turns a deliberate freeze into no limit at all, silently.
   */
  limitsForm: { maxProjects: number | null; projectsPerMinute: number | null; projectsPerHour: number | null } = {
    maxProjects: null,
    projectsPerMinute: null,
    projectsPerHour: null,
  };
  /** The values the form was loaded with, so "unchanged" is answerable. */
  private limitsLoaded = { maxProjects: null as number | null, projectsPerMinute: null as number | null, projectsPerHour: null as number | null };
  limitsReason = '';
  limitsSubmitting = false;
  limitsFeedback = '';
  limitsError = '';
  /** The limits block is collapsed by default — only its header row shows. */
  limitsOpen = false;

  /**
   * ⭐⭐ "Enter this account" — a one-time login link into the customer's platform
   * account (NEW PLATFORM only).
   *
   * ⚠️ `loginLinkUrl` IS DELIBERATELY HELD IN COMPONENT STATE AND RENDERED, which is
   * the opposite of how the project Open link is treated — see `openProject()` and
   * `ceo-open-project.service.ts`. The difference is what the URL contains: that one
   * wraps a permanent stored credential, this one a ticket the server mints per
   * click, keeps only as a SHA-256, kills on first use and expires in five minutes.
   * The whole point of the feature is that the admin can copy it.
   *
   * ⚠️ IT IS CLEARED WHENEVER THE PANEL CHANGES USER. A live link belonging to the
   * previous customer sitting under the next customer's name is the one way this
   * control could open the wrong account.
   */
  loginLinkUrl = '';
  loginLinkExpiresAt = '';
  loginLinkLoading = false;
  loginLinkNeedsPassword = false;
  loginLinkPassword = '';
  loginLinkError = '';
  /** The link is on the clipboard right now. Persists until the next mint or user change. */
  loginLinkCopied = false;
  /**
   * ⚠️ THE RAW LINK IS A FALLBACK, NOT THE NORMAL OUTPUT. One click should mint AND
   * copy — that is the whole point of the change — so the URL is only put on screen
   * when the clipboard write actually failed and the admin has nothing to paste.
   * Firefox drops transient user activation across the `await` on the mint request,
   * so this path is reachable in practice, not theoretical.
   */
  loginLinkRevealed = false;

  // Credits panel
  creditsMode: 'give' | 'remove' = 'give';
  creditsAmount: number | null = null;
  creditsSubmitting = false;
  creditsFeedback = '';
  creditsError = '';

  readonly formatEuros = formatEuros;
  readonly formatNumber = formatNumber;
  readonly formatCredits = formatCredits;
  readonly formatDate = formatDate;
  readonly planStyle = planStyle;
  readonly platformPlanLabel = platformPlanLabel;
  readonly acquisitionChipClass = acquisitionChipClass;
  readonly hostColor = hostColor;

  // ── Header plan badge + compact origin strip ──────────────────────────────────

  /**
   * The plan badge(s) beside the name. A platform account has ONE plan (the account
   * plan, comped ones say so); a legacy customer's plans belong to their PROJECTS,
   * so the distinct owned project plans are shown instead — never a Free badge they
   * were never on.
   */
  get headerPlanBadges(): { label: string; cls: string; title: string }[] {
    const u = this.user;
    if (!u) return [];
    if (u.platformAccount) {
      const plan = u.platformPlan || 'free';
      const status = u.platformPlanStatus && u.platformPlanStatus !== 'active' ? ` · ${u.platformPlanStatus}` : '';
      return [{
        label: platformPlanLabel(plan) + (u.platformPlanIsManual ? ' · comp' : ''),
        cls: planStyle(plan),
        title: `Platform plan: ${platformPlanLabel(plan)}${status}${u.platformPlanIsManual ? ' (granted by an admin, not invoiced)' : ''}`,
      }];
    }
    const plans = Array.from(new Set((u.plans || []).filter(Boolean)));
    return plans.slice(0, 3).map(plan => ({
      label: plan,
      cls: planStyle(plan),
      title: `Owns a project on the ${plan} plan`,
    }));
  }

  /**
   * The channel chip for the compact strip — same rule as the users table: the
   * unified channel name, coloured by kind, where `paid` comes from the measured
   * signals (utm medium / click ids) because the name never says.
   */
  get acquisitionChip(): { channel: string; kind: string } | null {
    const a = this.user?.acquisition;
    if (!a?.acquisitionChannel) return null;
    const medium = (a.utmMedium || '').toLowerCase();
    // `ref=ad` is our own ad-link marker (`?ref=ad&source=meta_ig`) — a paid click.
    const paid = /cpc|ppc|paid|ads?/.test(medium) || !!a.gclid || !!a.fbclid || !!a.ttclid || a.ref === 'ad';
    return { channel: a.acquisitionChannel, kind: acquisitionKindFor(a.acquisitionChannel, a.acquisitionCampaign, paid) };
  }

  /** The acquisition snapshot, for the profile facts. */
  get acq() {
    return this.user?.acquisition;
  }

  /** What they answered in the discovery modal, plus what they said they searched. */
  get selfReportedLabel(): string {
    const a = this.acq;
    if (!a?.selfReportedSource) return '';
    return a.selfReportedDetail ? `${a.selfReportedSource} — "${a.selfReportedDetail}"` : a.selfReportedSource;
  }

  get referrerLabel(): string {
    return this.acq?.referrer || this.acq?.httpReferer || '';
  }

  /** City + geo-IP country. The region is a numeric code (`01`) and says nothing to a person. */
  get locationLabel(): string {
    const a = this.acq;
    return [a?.city, countryName(a?.country)].filter(Boolean).join(', ');
  }

  get deviceLabel(): string {
    const a = this.acq;
    return [a?.deviceType || this.user?.registeredDevice, a?.deviceBrand].filter(Boolean).join(' · ');
  }

  get browserLabel(): string {
    const a = this.acq;
    const browser = [a?.browserName, a?.browserVersion].filter(Boolean).join(' ');
    const os = [a?.osName, a?.osVersion].filter(Boolean).join(' ');
    return [browser, os].filter(Boolean).join(' · ');
  }

  /** Who comped the plan, when and why — the tooltip on the compact plan strip's `comp` chip. */
  get planGrantTitle(): string {
    const u = this.user;
    if (!u?.platformPlanIsManual) return '';
    const parts = ['Granted by an admin, not invoiced'];
    if (u.platformPlanManualBy) parts.push(`by ${u.platformPlanManualBy}`);
    if (u.platformPlanManualAt) parts.push(`on ${formatDate(u.platformPlanManualAt)}`);
    if (u.platformPlanManualReason) parts.push(`— ${u.platformPlanManualReason}`);
    return parts.join(' ');
  }

  /** Blog paths lose their `/blog/` prefix, as in the users table; the marker is spelled out. */
  get acquisitionCampaignShort(): string {
    const value = this.user?.acquisition?.acquisitionCampaign || '';
    if (!value) return '';
    if (value === SELF_REPORTED_CAMPAIGN) return 'self-reported';
    return value.replace(/^\/(es\/)?blog\//, '');
  }

  get isSelfReportedAcquisition(): boolean {
    return this.user?.acquisition?.acquisitionCampaign === SELF_REPORTED_CAMPAIGN;
  }

  get acquisitionTitle(): string {
    const a = this.user?.acquisition;
    if (!a) return '';
    const query = a.selfReportedDetail ? `searched: "${a.selfReportedDetail}"` : '';
    return [a.acquisitionChannel, a.acquisitionCampaign, a.acquisitionDetail, query].filter(Boolean).join(' · ');
  }
  /** ISO-2 → English name. Passes free-text countries through untouched — see `countryName`. */
  readonly countryName = countryName;
  readonly projectCategoryLabel = projectCategoryLabel;
  readonly projectCategoryStyle = projectCategoryStyle;
  readonly projectCategoryTitle = projectCategoryTitle;
  /** Project chip text: 'ui' for platform-built projects, otherwise the plan. */
  readonly projectTypeLabel = projectTypeLabel;

  // ── Delete account (CEO-only; the server re-checks the CEO session + access password)
  deleteUserOpen = false;
  deleteUserConfirm = '';
  deleteUserPassword = '';
  deleteUserError: string | null = null;
  deletingUser = false;

  openDeleteUser(): void {
    this.deleteUserOpen = true;
    this.deleteUserConfirm = '';
    this.deleteUserPassword = this.openProjects.storedPassword;
    this.deleteUserError = null;
  }

  closeDeleteUser(): void {
    if (this.deletingUser) return;
    this.deleteUserOpen = false;
    this.deleteUserPassword = '';
  }

  get deleteUserConfirmed(): boolean {
    const email = (this.user?.email || '').trim().toLowerCase();
    return !!email && this.deleteUserConfirm.trim().toLowerCase() === email;
  }

  async confirmDeleteUser(): Promise<void> {
    const password = (this.deleteUserPassword || '').trim();
    if (!this.userId || !this.deleteUserConfirmed || !password || this.deletingUser) return;
    this.deletingUser = true;
    this.deleteUserError = null;
    let response: any;
    try {
      response = await this.apiSdkService.deleteCeoUser(this.userId, password, this.deleteUserConfirm.trim());
    } catch (err: any) {
      response = { error: { errors: { errorMessage: err?.message || 'The request failed.' } } };
    }
    this.deletingUser = false;
    if (response?.success?.data) {
      this.deleteUserOpen = false;
      this.creditsChanged.emit(); // parent refreshes its list
      this.close();
      return;
    }
    const errors = response?.error?.errors || response?.success?.errors || {};
    if (errors.errorCode === 'PASSWORD_INCORRECT') this.openProjects.forget();
    this.deleteUserError = errors.errorMessage || 'The user could not be deleted.';
  }

  // ── Delete all projects (FREE accounts only; the account, its keys and credits stay). Each call is re-checked on
  // the server: v2 projects on totalum-accounts (free plan, write password, typed e-mail), legacy ones one by one on
  // account-backend's CEO project delete (access password; paid legacy plans refused).
  deleteProjectsOpen = false;
  deleteProjectsConfirm = '';
  deleteProjectsPassword = '';
  deleteProjectsV2Password = '';
  deletingProjects = false;
  deleteProjectsProgress = '';
  deleteProjectsError: string | null = null;
  deleteProjectsResults: { projectId: string; deleted: boolean; error?: string }[] | null = null;
  /** The counts when the dialog opened (the list reloads after the run). */
  deleteProjectsCounts = { v2: 0, legacy: 0 };

  get v2Projects(): CeoProjectI[] {
    return this.projects.filter(p => p.engineVersion === 'v2');
  }

  get legacyProjects(): CeoProjectI[] {
    return this.projects.filter(p => p.engineVersion !== 'v2');
  }

  /** Conservative: a platform account on the free plan, or a legacy-only one, and every project on a free plan. */
  get canDeleteAllProjects(): boolean {
    const u = this.user;
    if (!u || !this.projects.length) return false;
    if (u.platformAccount && (u.platformPlan || 'free') !== 'free') return false;
    return this.projects.every(p => ['free', 'api', 'agent'].includes(String(p.plan)));
  }

  openDeleteProjects(): void {
    this.deleteProjectsOpen = true;
    this.deleteProjectsCounts = { v2: this.v2Projects.length, legacy: this.legacyProjects.length };
    this.deleteProjectsConfirm = '';
    this.deleteProjectsPassword = this.openProjects.storedPassword;
    this.deleteProjectsV2Password = this.ceoV2.password;
    this.deleteProjectsError = null;
    this.deleteProjectsResults = null;
    this.deleteProjectsProgress = '';
  }

  closeDeleteProjects(): void {
    if (this.deletingProjects) return;
    this.deleteProjectsOpen = false;
    this.deleteProjectsPassword = '';
    this.deleteProjectsV2Password = '';
  }

  get deleteProjectsConfirmed(): boolean {
    const email = (this.user?.email || '').trim().toLowerCase();
    return !!email && this.deleteProjectsConfirm.trim().toLowerCase() === email;
  }

  get deleteProjectsReady(): boolean {
    return this.deleteProjectsConfirmed && !this.deletingProjects
      && (!this.legacyProjects.length || !!this.deleteProjectsPassword.trim())
      && (!this.v2Projects.length || !!this.deleteProjectsV2Password.trim());
  }

  async confirmDeleteProjects(): Promise<void> {
    if (!this.userId || !this.deleteProjectsReady || !this.canDeleteAllProjects) return;
    const userId = this.userId;
    const email = this.deleteProjectsConfirm.trim();
    const legacy = this.legacyProjects.map(p => p.organizationId);
    const results: { projectId: string; deleted: boolean; error?: string }[] = [];
    this.deletingProjects = true;
    this.deleteProjectsError = null;
    this.deleteProjectsResults = results;
    try {
      if (this.v2Projects.length) {
        this.ceoV2.password = this.deleteProjectsV2Password.trim();
        for (;;) {
          this.deleteProjectsProgress = 'Deleting the new-engine (v2) projects…';
          const r = await this.ceoV2.write<{ results: { projectId: string; deleted: boolean; errorCode?: string; errorMessage?: string }[]; remaining: number }>(
            'POST', `v2/accounts/${encodeURIComponent(userId)}/delete-projects`,
            { password: this.ceoV2.password, reason: 'Delete all projects of a free account (CEO user panel)', confirm: email },
          );
          if (r.error || !r.data) {
            if (/^PASSWORD_(INCORRECT|REQUIRED)\b/.test(r.error || '')) this.ceoV2.password = '';
            this.deleteProjectsError = r.error || 'The v2 projects could not be deleted.';
            return;
          }
          for (const x of r.data.results) {
            results.push({ projectId: x.projectId, deleted: x.deleted, ...(x.deleted ? {} : { error: `${x.errorCode}: ${x.errorMessage}` }) });
          }
          if (!r.data.remaining || !r.data.results.some(x => x.deleted)) break;
        }
      }
      const password = this.deleteProjectsPassword.trim();
      for (const [i, organizationId] of legacy.entries()) {
        this.deleteProjectsProgress = `Deleting project ${i + 1} of ${legacy.length}: ${organizationId}`;
        let response: any;
        try {
          response = await this.apiSdkService.deleteCeoProject(organizationId, password, organizationId);
        } catch (err: any) {
          response = { error: { errors: { errorMessage: err?.message || 'The request failed.' } } };
        }
        if (response?.success?.data) {
          results.push({ projectId: organizationId, deleted: true });
          continue;
        }
        const errors = response?.error?.errors || response?.success?.errors || {};
        results.push({ projectId: organizationId, deleted: false, error: errors.errorMessage || 'Not deleted: no answer from account-backend. Try again.' });
        if (errors.errorCode === 'PASSWORD_INCORRECT' || errors.errorCode === 'TOO_MANY_ATTEMPTS') {
          this.openProjects.forget();
          this.deleteProjectsError = errors.errorMessage || 'Wrong access password.';
          return;
        }
      }
    } finally {
      this.deletingProjects = false;
      this.deleteProjectsProgress = '';
      if (results.some(r => r.deleted)) {
        this.creditsChanged.emit(); // the parent refreshes its row
        await this.load();
      }
    }
  }

  get deleteProjectsSummary(): string {
    const r = this.deleteProjectsResults || [];
    const ok = r.filter(x => x.deleted).length;
    return `${ok} deleted, ${r.length - ok} failed`;
  }

  constructor(
    private apiSdkService: ApiSdkService,
    private openProjects: CeoOpenProjectService,
    private ceoV2: CeoV2Service,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId']) {
      this.contactOpen = false;
      if (this.userId) {
        this.resetState();
        this.load();
      } else {
        this.detail = null;
      }
    }
  }

  private resetState(): void {
    this.detail = null;
    this.deleteUserOpen = false;
    this.deleteUserError = null;
    this.deleteProjectsOpen = false;
    this.deleteProjectsResults = null;
    this.errorMessage = '';
    this.activeTab = 'purchases';
    this.projectSearch = '';
    this.projectsShown = 6;
    this.creditsMode = 'give';
    this.creditsAmount = null;
    this.creditsFeedback = '';
    this.creditsError = '';
    this.planChoice = '';
    this.planReason = '';
    this.planFeedback = '';
    this.planError = '';
    this.limitsForm = { maxProjects: null, projectsPerMinute: null, projectsPerHour: null };
    this.limitsLoaded = { maxProjects: null, projectsPerMinute: null, projectsPerHour: null };
    this.limitsReason = '';
    this.limitsFeedback = '';
    this.limitsError = '';
    this.limitsOpen = false;
    /*
      ⚠️ THE LOGIN LINK IS DROPPED ON EVERY USER CHANGE. It signs whoever clicks it
      into ONE specific account; leaving it on screen while the panel now shows a
      different customer is the single way this control could open the wrong one.
    */
    this.loginLinkUrl = '';
    this.loginLinkExpiresAt = '';
    this.loginLinkNeedsPassword = false;
    this.loginLinkPassword = '';
    this.loginLinkError = '';
    this.loginLinkCopied = false;
    this.loginLinkRevealed = false;
  }

  /**
   * Mint a one-time link into this customer's platform account AND put it straight
   * on the clipboard.
   *
   * ⚠️ IT DOES NOT OPEN A TAB, unlike `openProject`. The admin asked for a link they
   * can paste into another window — often a different browser or profile, precisely
   * so the impersonated session does not sit alongside their own.
   *
   * ⚠️⚠️ ONE CLICK, NOT THREE. The link used to be rendered and then copied by hand,
   * which is two extra actions on the one control that exists to be fast when a
   * customer is waiting. Nothing is lost by not showing it: the URL carries a ticket
   * the server minted for this click, kills on first use and drops in five minutes —
   * there is nothing in it an admin ever needs to read.
   *
   * ⚠️ THE COPY IS ATTEMPTED, NOT ASSUMED. If the clipboard refuses (Firefox drops
   * transient activation across this `await`; any non-HTTPS origin has no clipboard
   * at all) the URL is REVEALED instead, so a failure costs a manual copy rather
   * than the link itself.
   */
  async createLoginLink(password?: string): Promise<void> {
    if (!this.userId || this.loginLinkLoading) return;

    this.loginLinkLoading = true;
    this.loginLinkError = '';
    this.loginLinkUrl = '';
    this.loginLinkCopied = false;
    this.loginLinkRevealed = false;

    try {
      const outcome = await this.openProjects.platformLoginUrl(this.userId, password);

      this.loginLinkNeedsPassword = outcome.needsPassword;
      this.loginLinkError = outcome.error || '';

      if (outcome.url) {
        this.loginLinkUrl = outcome.url;
        this.loginLinkExpiresAt = outcome.expiresAt || '';
        this.loginLinkPassword = '';

        const copied = await this.writeToClipboard(outcome.url);
        this.loginLinkCopied = copied;
        this.loginLinkRevealed = !copied;
      }
    } catch (error: any) {
      console.error('Error creating the platform login link:', error);
      this.loginLinkError = error?.message || 'Could not create the login link.';
    } finally {
      this.loginLinkLoading = false;
    }
  }

  /** The fallback Copy button, shown only when the automatic copy did not work. */
  async copyLoginLink(): Promise<void> {
    if (!this.loginLinkUrl) return;

    const copied = await this.writeToClipboard(this.loginLinkUrl);
    this.loginLinkCopied = copied;
    if (!copied) {
      this.loginLinkError = 'Could not copy. Select the link and copy it manually.';
    }
  }

  /** `false` rather than a throw — every caller here wants to degrade, not fail. */
  private async writeToClipboard(value: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (error) {
      console.error('Could not copy the login link:', error);
      return false;
    }
  }

  async load(): Promise<void> {
    if (!this.userId) return;
    this.loading = true;
    try {
      const result = await this.apiSdkService.getCeoUserDetail(this.userId);
      const detail: CeoUserDetailI | undefined = result?.success?.data;
      if (detail) {
        this.detail = detail;
        // Pre-select the plan they are on, so the dropdown reads as "current" and a
        // stray Apply cannot move an account the admin never meant to touch.
        this.planChoice = detail.user?.platformPlan || 'free';
        this.hydrateLimitsForm();
        // Only platform accounts can be re-planned, so only they pay for the lookup.
        if (detail.user?.platformAccount) this.loadPlanUsage();
      } else {
        this.errorMessage = result?.error?.errors || 'Could not load this user';
      }
    } catch (e) {
      console.error('Error loading CEO user detail:', e);
      this.errorMessage = 'Could not load this user';
    } finally {
      this.loading = false;
    }
  }

  close(): void {
    this.closed.emit();
  }

  get user() {
    return this.detail?.user;
  }

  get projects(): CeoProjectI[] {
    return this.detail?.user?.projects || [];
  }

  /** Search matches the name, the description and the organizationId. */
  get filteredProjects(): CeoProjectI[] {
    const term = this.projectSearch.trim().toLowerCase();
    if (!term) return this.projects;
    return this.projects.filter(p =>
      (p.description || '').toLowerCase().includes(term) ||
      (p.projectDescription || '').toLowerCase().includes(term) ||
      (p.organizationId || '').toLowerCase().includes(term)
    );
  }

  get visibleProjects(): CeoProjectI[] {
    return this.filteredProjects.slice(0, this.projectsShown);
  }

  get hasMoreProjects(): boolean {
    return this.filteredProjects.length > this.projectsShown;
  }

  showMoreProjects(): void {
    this.projectsShown += this.PROJECTS_STEP;
  }

  // ── Opening a customer's project ─────────────────────────────────────────
  /**
   * ⭐ ENTERS THE PROJECT ITSELF, not the admin panel's tables view.
   *
   * ⚠️ THE SAME FLOW AS THE PROJECTS PAGE, DOWN TO THE STATE NAMES — one shared
   * service (`ceo-open-project.service.ts`) does the fetch, the password memory and
   * the `window.open`. Two copies of the credential handling is exactly how one of
   * them ends up without the lockout or with the password logged.
   */
  openingProjectId: string | null = null;
  openError: string | null = null;
  passwordFor: CeoProjectI | null = null;
  passwordInput = '';
  passwordError: string | null = null;

  async openProject(project: CeoProjectI): Promise<void> {
    if (!project?.organizationId || this.openingProjectId) return;

    this.openError = null;
    this.openingProjectId = project.organizationId;

    const outcome = await this.openProjects.open(project.organizationId);
    this.openingProjectId = null;

    if (outcome.needsPassword) {
      this.passwordFor = project;
      this.passwordInput = '';
      this.passwordError = outcome.error;
      return;
    }
    if (!outcome.opened) this.openError = outcome.error;
  }

  async submitPassword(): Promise<void> {
    const project = this.passwordFor;
    const password = (this.passwordInput || '').trim();
    if (!project?.organizationId || !password || this.openingProjectId) return;

    this.passwordError = null;
    this.openingProjectId = project.organizationId;

    const outcome = await this.openProjects.open(project.organizationId, password);
    this.openingProjectId = null;

    if (outcome.opened) {
      this.closePasswordPanel();
      return;
    }
    if (outcome.needsPassword) this.passwordError = outcome.error || 'That password is not correct.';
    else {
      this.closePasswordPanel();
      this.openError = outcome.error;
    }
  }

  closePasswordPanel(): void {
    this.passwordFor = null;
    this.passwordInput = '';
    this.passwordError = null;
  }

  setTab(tab: DetailTab): void {
    this.activeTab = tab;
  }

  get purchases(): CeoPurchaseI[] {
    return this.detail?.purchases || [];
  }

  /** Every populated row across the Acquisition tab's groups — the tab badge count. */
  get acquisitionRows(): AcquisitionRow[] {
    // No `flatMap`: this project's TS lib stops at ES2017.
    return this.acquisitionGroups.reduce((all: AcquisitionRow[], g) => all.concat(g.rows), []);
  }

  /**
   * ⭐ THE ACQUISITION TAB, as titled groups: the answer first, then the evidence
   * that produced it, then the browser fingerprint. Empty fields are skipped; a
   * group with nothing in it is dropped, except where its ABSENCE is itself a fact
   * (no browser snapshot at all) — then it stays with a note saying so.
   *
   * ⚠️ THE FINGERPRINT IS WHAT THE BROWSER CLAIMED. Screen size, cores, memory and
   * the automation flags come from client-side JS, so they corroborate — they never
   * prove anything, and a missing flag is not innocence.
   */
  get acquisitionGroups(): AcquisitionGroup[] {
    const a = this.user?.acquisition;
    if (!a) return [];

    const d = (a.landingDevice || {}) as Record<string, any>;
    const s = (a.landingSignals || {}) as Record<string, any>;
    const yes = (flag: any) => (flag === 1 || flag === true ? 'yes' : '');
    const px = (n: any) => (typeof n === 'number' ? `${n} px` : '');

    const groups: AcquisitionGroup[] = [
      {
        title: 'Attribution',
        rows: [
          { label: 'Channel', value: a.acquisitionChannel },
          { label: 'Campaign', value: a.acquisitionCampaign },
          { label: 'Detail', value: a.acquisitionDetail },
          { label: 'Measured bucket', value: [a.channel, a.sourceLabel].filter(Boolean).join(' · ') },
          { label: 'Measured detail', value: a.sourceDetail },
          { label: 'Self-reported', value: a.selfReportedSource },
          { label: 'Searched for', value: a.selfReportedDetail },
          { label: 'Answered', value: formatDate(a.selfReportedAt) },
        ],
      },
      {
        title: 'Campaign signals',
        rows: [
          { label: 'UTM source', value: a.utmSource },
          { label: 'UTM medium', value: a.utmMedium },
          { label: 'UTM campaign', value: a.utmCampaign },
          { label: 'UTM content', value: a.utmContent },
          { label: 'UTM term', value: a.utmTerm },
          { label: 'Landing source', value: a.source },
          { label: 'Landing campaign', value: a.campaign },
          { label: 'Referral code', value: a.ref },
          { label: 'Google click id', value: a.gclid, mono: true },
          { label: 'Meta click id', value: a.fbclid, mono: true },
          { label: 'TikTok click id', value: a.ttclid, mono: true },
          { label: 'Reddit click id', value: a.rdtCid, mono: true },
          { label: 'URL tags', value: this.tagsLabel(a.tags), mono: true, wide: true },
        ],
      },
      {
        title: 'Journey',
        rows: [
          { label: 'Referrer', value: a.referrer, wide: true },
          { label: 'Landing path', value: a.landingPath, wide: true },
          { label: 'Signup request referer', value: a.httpReferer, wide: true },
          { label: 'First visit', value: formatDate(a.firstVisitAt) },
          { label: 'Visits before signup', value: a.visits },
          { label: 'Last visit', value: formatDate(a.lastVisitAt) },
          { label: 'Last touch', value: [a.lastTouchChannel, a.lastTouchSourceLabel].filter(Boolean).join(' · ') },
          { label: 'Last touch UTM', value: [a.lastTouchUtmSource, a.lastTouchUtmCampaign].filter(Boolean).join(' · ') },
          { label: 'Last touch referrer', value: a.lastTouchReferrer },
          { label: 'Last touch at', value: formatDate(a.lastTouchAt) },
          { label: 'Captured', value: formatDate(a.capturedAt) },
        ],
      },
      {
        title: 'Network',
        rows: [
          { label: 'IP', value: this.ipLabel(a.ip), mono: true, tone: this.isLoopbackIp(a.ip) ? 'muted' : undefined },
          // Pure ISO-2 here (geoip), unlike the free-text profile country.
          { label: 'Country (IP)', value: countryName(a.country) },
          { label: 'Region', value: a.region },
          { label: 'City', value: a.city },
        ],
      },
      {
        title: 'Browser fingerprint',
        note: Object.keys(d).length ? undefined
          : 'The browser sent no device snapshot — the signup never ran the website/platform capture script.',
        rows: [
          { label: 'Screen', value: this.screenLabel(d) },
          { label: 'Viewport width', value: px(d['viewportW']) },
          { label: 'Pixel ratio', value: d['dpr'] ? `${d['dpr']}×` : '' },
          { label: 'Physical resolution', value: this.physicalLabel(d) },
          { label: 'CPU cores', value: d['cores'] },
          { label: 'Memory', value: d['mem'] ? `${d['mem']} GB` : '' },
          { label: 'Platform', value: d['platform'] },
          { label: 'Device (browser)', value: d['type'] },
          { label: 'Device (user agent)', value: [a.deviceType, a.deviceBrand].filter(Boolean).join(' · ') },
          { label: 'Browser', value: [a.browserName, a.browserVersion].filter(Boolean).join(' ') },
          { label: 'OS', value: [a.osName, a.osVersion].filter(Boolean).join(' ') },
          { label: 'Language', value: d['lang'] },
          { label: 'Languages', value: d['langs'] },
          { label: 'Timezone', value: this.timezoneLabel(d) },
          { label: 'User agent', value: a.userAgent, mono: true, wide: true },
        ],
      },
      {
        title: 'Automation signals',
        note: Object.keys(s).length ? 'Evidence to weigh, not a verdict. Only webdriver is unambiguous; a missing flag proves nothing.'
          : 'No automation signals were reported for this signup.',
        rows: [
          { label: 'WebDriver (automated browser)', value: yes(s['webdriver']) ? 'YES' : '', tone: 'danger' },
          { label: 'Embedded (iframe / webview)', value: yes(s['embedded']), tone: 'warn' },
          { label: 'Empty language list', value: yes(s['noLangs']), tone: 'warn' },
          { label: 'Browser plugins', value: s['plugins'] },
          { label: 'Interacted with the page', value: s['interacted'] ? 'yes' : (Object.keys(s).length ? 'no' : '') },
          { label: 'Time to first interaction', value: typeof s['msToInteract'] === 'number' ? `${(s['msToInteract'] / 1000).toFixed(1)} s` : '' },
        ],
      },
      {
        title: 'Ad identifiers & consent',
        rows: [
          { label: 'Ad consent', value: a.adConsent === true ? 'accepted' : a.adConsent === false ? 'rejected' : '',
            tone: a.adConsent === false ? 'warn' : undefined },
          { label: 'GA client id', value: a.gaClientId, mono: true },
          { label: 'Meta browser id (_fbp)', value: a.fbp, mono: true },
          { label: 'Meta click cookie (_fbc)', value: a.fbc, mono: true, wide: true },
          { label: 'Reddit pixel id', value: a.rdtUuid, mono: true, wide: true },
          { label: 'Reddit conversion id', value: a.rdtConversionId, mono: true },
        ],
      },
    ];

    return groups
      .map(g => ({
        ...g,
        rows: g.rows
          .filter(r => r.value !== undefined && r.value !== null && String(r.value).trim() !== '')
          .map(r => ({ ...r, value: String(r.value) })),
      }))
      .filter(g => g.rows.length || g.note);
  }

  private tagsLabel(tags?: Record<string, string>): string {
    if (!tags) return '';
    return Object.entries(tags).map(([k, v]) => `${k}=${v}`).join('  ');
  }

  /** `::1` / 127.x is the SERVER's own address — what was recorded before the 2026-09-10 client-IP fix. */
  isLoopbackIp(ip?: string): boolean {
    return !!ip && /^(::1|127\.|::ffff:127\.)/.test(ip.trim());
  }

  ipLabel(ip?: string): string {
    if (!ip) return '';
    return this.isLoopbackIp(ip) ? `${ip} — not the visitor's (not recorded)` : ip;
  }

  private screenLabel(d: Record<string, any>): string {
    if (!d['screenW']) return '';
    return d['screenH'] ? `${d['screenW']} × ${d['screenH']} px` : `${d['screenW']} px wide`;
  }

  /** CSS size × pixel ratio — what actually tells two phones apart. */
  private physicalLabel(d: Record<string, any>): string {
    const dpr = Number(d['dpr']);
    if (!d['screenW'] || !dpr || dpr === 1) return '';
    const w = Math.round(d['screenW'] * dpr);
    return d['screenH'] ? `${w} × ${Math.round(d['screenH'] * dpr)} px` : `${w} px wide`;
  }

  /** "Asia/Riyadh (UTC+3)" — `tzOff` is minutes EAST of UTC. */
  private timezoneLabel(d: Record<string, any>): string {
    const tz = d['tz'] || '';
    if (typeof d['tzOff'] !== 'number') return tz;
    const off = d['tzOff'];
    const sign = off >= 0 ? '+' : '-';
    const h = Math.floor(Math.abs(off) / 60);
    const m = Math.abs(off) % 60;
    const utc = `UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
    return tz ? `${tz} (${utc})` : utc;
  }

  // ── Plan (NEW PLATFORM, account-scoped) ─────────────────────────────────────────

  /**
   * ⚠️ GATED ON `platformAccount`, NOT ON THE PRESENCE OF A PLAN. A legacy customer
   * has no `users.plan` at all — their plans belong to their projects — and offering
   * this control for them would write a field nothing legacy reads and start the
   * renewal cron granting credits against it.
   */
  get isPlatformUser(): boolean {
    return this.detail?.user?.platformAccount === true;
  }

  get planChanges(): CeoPlanChangeI[] {
    return this.detail?.planChanges || [];
  }

  /** The plans an admin may grant. Server-supplied, so the ladder cannot drift. */
  get grantablePlans(): { id: string; name: string; creditsPerMonth: number }[] {
    return this.planUsage?.plans || [];
  }

  get planUnchanged(): boolean {
    return !this.planChoice || this.planChoice === (this.detail?.user?.platformPlan || 'free');
  }

  get planBudgetExhausted(): boolean {
    return !!this.planUsage && this.planUsage.used >= this.planUsage.max;
  }

  /**
   * ⚠️ THE STRIPE BLOCK IS SHOWN BEFORE THE ATTEMPT, NOT AFTER IT. The server
   * refuses these accounts anyway, but letting an admin pick a plan, type a reason
   * and press Apply only to be told the change was never possible wastes their time
   * and reads as a bug.
   */
  get planBlockedByStripe(): boolean {
    return this.detail?.user?.platformHasStripeSubscription === true;
  }

  private async loadPlanUsage(): Promise<void> {
    try {
      const result = await this.apiSdkService.getCeoPlanChangeUsage();
      this.planUsage = result?.success?.data || null;
    } catch (e) {
      console.error('Error loading the plan-change usage:', e);
      this.planUsage = null;
    }
  }

  /**
   * Applies a non-invoiced plan change.
   *
   * ⚠️ THE REFUSALS ARE READ BY CODE, NOT BY MESSAGE. `HAS_STRIPE_SUBSCRIPTION` and
   * `DAILY_LIMIT_REACHED` need different reactions from the admin — one means "go to
   * Stripe", the other "come back tomorrow" — so the panel keeps them distinguishable
   * rather than flattening both into a red line.
   */
  async submitPlan(): Promise<void> {
    if (!this.userId || this.planUnchanged || this.planSubmitting) return;

    this.planSubmitting = true;
    this.planError = '';
    this.planFeedback = '';

    try {
      const result = await this.apiSdkService.setCeoUserPlan(this.userId, this.planChoice, this.planReason);
      const data = result?.success?.data;

      if (!data?.ok) {
        const errors = result?.error?.errors || result?.success?.errors || {};
        this.planError = errors.errorMessage || 'The plan change failed';
        if (errors.usage) this.planUsage = { ...(this.planUsage as any), ...errors.usage };
        return;
      }

      this.planUsage = this.planUsage ? { ...this.planUsage, ...data.usage } : data.usage;
      this.planFeedback = `Moved to ${platformPlanLabel(data.plan)}` +
        (data.recurrentCredits ? ` — ${formatCredits(data.recurrentCredits)} plan credits granted.` : '.');
      this.planReason = '';

      // The balance, the plan badge and the audit list all just changed; the row
      // behind the panel shows the plan too, so the table is refreshed as well.
      this.creditsChanged.emit();
      await this.load();
    } catch (e) {
      console.error('Error changing the plan:', e);
      this.planError = 'The plan change failed';
    } finally {
      this.planSubmitting = false;
    }
  }

  // ── Project limits (NEW PLATFORM, account-scoped) ───────────────────────────────

  get projectLimits(): CeoProjectLimitsI | null {
    return this.detail?.projectLimits || null;
  }

  get limitsLadder(): CeoProjectLimitTierI[] {
    return this.projectLimits?.ladder || [];
  }

  /**
   * The account's OWN override values, hydrated into the form.
   *
   * ⚠️ IT READS `overridden`, NOT THE RESOLVED NUMBERS. The panel shows the
   * resolved value as a PLACEHOLDER when nothing is pinned, so filling the boxes
   * with the plan's defaults would turn "no override" into three overrides the
   * moment anyone pressed Save — pinning today's plan numbers onto the account
   * forever, invisibly, including through a later plan change.
   */
  private hydrateLimitsForm(): void {
    const limits = this.projectLimits;
    const value = (key: 'maxProjects' | 'projectsPerMinute' | 'projectsPerHour'): number | null => {
      if (!limits?.overridden?.[key]) return null;
      const raw = limits[key];
      return typeof raw === 'number' ? raw : null;
    };

    this.limitsForm = {
      maxProjects: value('maxProjects'),
      projectsPerMinute: value('projectsPerMinute'),
      projectsPerHour: value('projectsPerHour'),
    };
    this.limitsLoaded = { ...this.limitsForm };
  }

  /** `unlimited` is a string sentinel from the server, never a number. */
  get maxProjectsLabel(): string {
    const max = this.projectLimits?.maxProjects;
    if (max === undefined || max === null) return '—';
    return max === 'unlimited' ? '∞' : String(max);
  }

  get projectsAtCeiling(): boolean {
    const limits = this.projectLimits;
    if (!limits || typeof limits.maxProjects !== 'number') return false;
    return (limits.used ?? 0) >= limits.maxProjects;
  }

  get hasAnyOverride(): boolean {
    const o = this.projectLimits?.overridden;
    return !!(o?.maxProjects || o?.projectsPerMinute || o?.projectsPerHour);
  }

  get limitsUnchanged(): boolean {
    return (
      this.normalizeLimit(this.limitsForm.maxProjects) === this.limitsLoaded.maxProjects &&
      this.normalizeLimit(this.limitsForm.projectsPerMinute) === this.limitsLoaded.projectsPerMinute &&
      this.normalizeLimit(this.limitsForm.projectsPerHour) === this.limitsLoaded.projectsPerHour
    );
  }

  /**
   * An empty box is `null`; everything else keeps its number — INCLUDING `0`.
   *
   * ⚠️ Angular's `type="number"` binding yields `null` for an empty field but can
   * also yield the empty STRING mid-edit, and `Number('')` is `0`. Without the
   * explicit `''` test, clearing a box would submit a freeze.
   */
  private normalizeLimit(value: number | null | string): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }

  /** The plan's own number for one field, shown as the input's placeholder. */
  planDefaultLabel(key: 'maxProjects' | 'projectsPerMinute' | 'projectsPerHour'): string {
    const defaults = this.projectLimits?.planDefaults;
    if (!defaults) return 'plan default';
    const raw = defaults[key];
    return raw === 'unlimited' ? 'unlimited' : `${raw} (plan)`;
  }

  /** "Basic allows 50 projects · 10 projects per minute · 120/hour." */
  get currentPlanLimitsSentence(): string {
    const plan = this.user?.platformPlan;
    const tier = this.limitsLadder.find(t => t.id === plan);
    if (!tier) return '';
    const max = tier.maxProjects === 'unlimited' ? 'unlimited projects' : `${tier.maxProjects} projects`;
    return `${tier.name} allows ${max} · ${tier.rateLabel} · ${tier.projectsPerHour}/hour.`;
  }

  async submitProjectLimits(): Promise<void> {
    if (!this.userId || this.limitsSubmitting) return;
    await this.writeProjectLimits({
      maxProjects: this.normalizeLimit(this.limitsForm.maxProjects),
      projectsPerMinute: this.normalizeLimit(this.limitsForm.projectsPerMinute),
      projectsPerHour: this.normalizeLimit(this.limitsForm.projectsPerHour),
    });
  }

  /** Clears all three overrides — the account goes back to whatever its plan says. */
  async clearProjectLimits(): Promise<void> {
    if (!this.userId || this.limitsSubmitting) return;
    await this.writeProjectLimits({ maxProjects: null, projectsPerMinute: null, projectsPerHour: null });
  }

  private async writeProjectLimits(limits: {
    maxProjects: number | null;
    projectsPerMinute: number | null;
    projectsPerHour: number | null;
  }): Promise<void> {
    this.limitsSubmitting = true;
    this.limitsError = '';
    this.limitsFeedback = '';

    try {
      const result = await this.apiSdkService.setCeoUserProjectLimits(this.userId!, limits, this.limitsReason);
      const data = result?.success?.data;

      if (!data?.ok) {
        const errors = result?.error?.errors || result?.success?.errors;
        this.limitsError = (typeof errors === 'string' ? errors : errors?.errorMessage) || 'Could not save the limits';
        return;
      }

      this.limitsFeedback = 'Limits saved.';
      this.limitsReason = '';
      // The panel shows resolved values, the override flags and the usage count —
      // all three just changed, so the detail is re-read rather than patched.
      await this.load();
    } catch (e) {
      console.error('Error saving the project limits:', e);
      this.limitsError = 'Could not save the limits';
    } finally {
      this.limitsSubmitting = false;
    }
  }

  // ── Credits ─────────────────────────────────────────────────────────────────────

  setCreditsMode(mode: 'give' | 'remove'): void {
    this.creditsMode = mode;
    this.creditsError = '';
    this.creditsFeedback = '';
  }

  get creditsAmountInvalid(): boolean {
    const n = this.creditsAmount;
    return n === null || !Number.isInteger(n) || n < 1 || n > 500000;
  }

  async submitCredits(): Promise<void> {
    if (!this.userId || this.creditsAmountInvalid || this.creditsSubmitting) return;

    this.creditsSubmitting = true;
    this.creditsError = '';
    this.creditsFeedback = '';

    try {
      const amount = this.creditsAmount as number;
      const result = this.creditsMode === 'give'
        ? await this.apiSdkService.giveCreditsToCeoUser(this.userId, amount)
        : await this.apiSdkService.removeCreditsFromCeoUser(this.userId, amount);

      const data = result?.success?.data;
      if (!data) {
        this.creditsError = result?.error?.errors || 'The credit adjustment failed';
        return;
      }

      // Reflect the new balance locally instead of refetching the whole detail payload.
      if (this.detail?.user) {
        this.detail.user.credits = data.newBalance;
      }
      this.creditsFeedback = this.creditsMode === 'give'
        ? `Gave ${amount} credits. New balance: ${formatCredits(data.newBalance)}`
        : `Removed ${data.removed ?? amount} credits. New balance: ${formatCredits(data.newBalance)}`;
      this.creditsAmount = null;
      this.creditsChanged.emit();

      // Refresh history so the new audit row shows up in the credits tab.
      this.load();
    } catch (e) {
      console.error('Error adjusting credits:', e);
      this.creditsError = 'The credit adjustment failed';
    } finally {
      this.creditsSubmitting = false;
    }
  }
}
